import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { EyengageFilterCategory } from 'src/shared/types/src';
import { CustomButton, CustomCheckbox } from '../commonComponents';
import { getEyengageFilterCategories } from 'src/shared/redux-state/src';
import { useAppSelector } from 'src/app/services/store';
import { CheckmarkCircle } from 'react-ionicons';

type EyengageFilterModalProps = {
    isOpen: boolean,
    toggleCategoryModal: () => void,
    selectedCategoriesArray?: EyengageFilterCategory[];
    onCategorySelection: (categories: EyengageFilterCategory[]) => void;
}

const EyengagePostCategoryListModal: React.FC<EyengageFilterModalProps> = React.memo(({ isOpen, toggleCategoryModal, selectedCategoriesArray, onCategorySelection}) => {

    const filterCategories = useAppSelector(getEyengageFilterCategories);

    const [selectedCategories, setSelectedCategories] = useState<EyengageFilterCategory[]>([])

    useEffect(() => {
        if (!isOpen) return;
        setSelectedCategories(selectedCategoriesArray || [])
    }, [isOpen, selectedCategoriesArray])

    const sections = useMemo(() => {
        const grouped = filterCategories.reduce<Record<string, EyengageFilterCategory[]>>((acc, item) => {
            if (!acc[item.group]) acc[item.group] = [];
            acc[item.group].push(item);
            return acc;
        }, {});

        return Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .map(group => ({
                title: group,
                data: grouped[group].sort((a, b) => a.text.localeCompare(b.text))
            }));
    }, [filterCategories]);

    const onDonePress = () => {
        if (selectedCategories) {
            onCategorySelection(selectedCategories);
        }
    };

    const _renderCategory = ({ item, index }: { item: EyengageFilterCategory, index: number }) => {
        const isSelected = selectedCategories.some((category) => category.id === item.id)
        return (
            <div className={`d-flex align-items-center notification-ui cursor-pointer ${isSelected ? 'active' : ''}`}
                onClick={() => {
                    if (isSelected) {
                        setSelectedCategories((oldValues) => {
                            return oldValues.filter((obj) => obj.id !== item.id)
                        })
                    } else {
                        setSelectedCategories((oldValues) => {  
                            return [...oldValues, item]
                        })
                    }
                }}
            >
                <div className='notification-text'>{item.text}</div>
                <CustomCheckbox
                    isChecked={isSelected}
                />
            </div>
        )
    }

    const handleGroupSelection = useCallback((section: any) => {
        const sectionIds = section.data.map((c: any) => c.id);

        const selectedIds = selectedCategories.map((c) => c.id);

        const isAllSelected = sectionIds.every((id: any) => selectedIds.includes(id));

        let updated: EyengageFilterCategory[] = [];

        if (isAllSelected) {
            updated = selectedCategories.filter(
                (cat) => !sectionIds.includes(cat.id)
            );
        } else {
            const sectionSet = new Set(sectionIds);
            const nonSection = selectedCategories.filter(
                (cat) => !sectionSet.has(cat.id)
            );
            updated = [...nonSection, ...section.data];
        }

        setSelectedCategories(updated);
    }, [selectedCategories]);

    const isAnyChildSelected = useCallback((section: any) => {
        const allSectionIds = section?.data?.map((val: any) => val.id) || []
        const isSelected = selectedCategories?.some((category) => allSectionIds.includes(category.id))
        return isSelected 
    }, [selectedCategories])

    const isAllChildSelected = useCallback((section: any) => {
        const allSelectedIds = selectedCategories?.map((val: any) => val.id) || []
        const isSelected = section?.data?.every((category: any) => allSelectedIds.includes(category.id))
        return isSelected 
    }, [selectedCategories])

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggleCategoryModal} 
            backdrop={true}
            fade={false}
            className={`custom-modal-md modal-dialog-bottom modal-dialog-bottom-style`}
            modalClassName={`modal-slide-down`}
        >
            <ModalBody className='mg-t-15 pd-x-0'>
                <div className='d-flex justify-content-center align-items-center w-100 border-bottom border-gray-600 pd-b-10'>
                    <p className='fs-16 fw-semibold text-black mb-0'>Select Category</p>
                </div>
                <div className='category-list'>
                    {
                        sections.map((section, index) => {
                            return (
                                <>
                                    <div className={'d-flex align-items-center cursor-pointer notification-ui-group pd-y-10'} onClick={() => handleGroupSelection(section)}>
                                        <div className='fw-semibold text-black flex-1'>{section.title}</div>
                                        {/* <div className='ml-auto'> */}
                                            <CustomCheckbox
                                                isChecked={isAnyChildSelected(section)}
                                                intermediate={!isAllChildSelected(section)}
                                            />
                                        {/* </div> */}
                                    </div>
                                    <div>
                                        {section.data.map((item, index) => _renderCategory({ item, index }))}
                                    </div>
                                </>
                            )
                        })
                    }
                </div>
            </ModalBody>
            <ModalFooter>
                <CustomButton text="Done" color="primary" onClick={onDonePress} />
            </ModalFooter>
        </Modal>
    )
})

export default EyengagePostCategoryListModal;